import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { CURRENCY } from 'App';
import { CashoutOffer, cashoutTicket } from 'services/api/slipService';
import styles from './CashoutButton.module.css';

interface Props {
  offer?: CashoutOffer;
  /** Called after a cash out went through (reload the tickets). */
  onCashedOut: () => void;
  compact?: boolean;
}

type Step =
  | { kind: 'idle' }
  | { kind: 'confirm'; amount: number; changed?: boolean }
  | { kind: 'waiting'; amount: number; seconds: number };

/** Seconds left of the server's check, 5, 4 ... down to 1. */
const Countdown = ({ seconds }: { seconds: number }) => {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => {
      const gone = Math.floor((Date.now() - started) / 1000);
      setLeft(Math.max(1, seconds - gone));
    }, 250);
    return () => window.clearInterval(id);
  }, [seconds]);
  return <strong className={styles.countdown}>{left}</strong>;
};

/**
 * The cash out button of an open ticket: offer -> confirm -> the server's
 * check (a few seconds) -> paid, or a new offer to confirm.
 */
export const CashoutButton = ({ offer, onCashedOut, compact }: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>({ kind: 'idle' });

  // During the storno time the ticket shows Storno instead; system tickets
  // are not cashed out.
  if (!offer || offer.reason === 'storno' || offer.reason === 'system')
    return null;
  const amount = offer.available ? Number(offer.amount) : 0;
  const money = (n: number) => `${n.toFixed(2)} ${CURRENCY}`;

  const accept = async (value: number) => {
    const seconds = Math.max(1, Math.ceil((offer.wait_ms ?? 0) / 1000));
    setStep({ kind: 'waiting', amount: value, seconds });
    const res = await cashoutTicket(offer.ticket_id, value);
    if (res.ok) {
      setStep({ kind: 'idle' });
      toast.success(t('cash_out_success_label', { amount: money(res.amount) }));
      onCashedOut();
      return;
    }
    if (res.offer?.available && res.offer.amount) {
      setStep({
        kind: 'confirm',
        amount: Number(res.offer.amount),
        changed: true,
      });
      return;
    }
    setStep({ kind: 'idle' });
    toast.error(t('cash_out_unavailable_label'));
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  if (step.kind === 'waiting') {
    return (
      <div
        className={classNames(styles.box, compact && styles.compact)}
        onClick={stop}
      >
        <span className={styles.waiting}>
          {t('cash_out_waiting_label')} {money(step.amount)}
        </span>
        <Countdown seconds={step.seconds} />
      </div>
    );
  }

  if (step.kind === 'confirm') {
    return (
      <div
        className={classNames(styles.box, compact && styles.compact)}
        onClick={stop}
      >
        <span className={styles.question}>
          {step.changed
            ? t('cash_out_changed_label', { amount: money(step.amount) })
            : t('cash_out_confirm_label', { amount: money(step.amount) })}
        </span>
        <button
          type="button"
          className={styles.yes}
          onClick={() => accept(step.amount)}
        >
          {t('yes_label', { defaultValue: 'Yes' })}
        </button>
        <button
          type="button"
          className={styles.no}
          onClick={() => setStep({ kind: 'idle' })}
        >
          {t('no_label', { defaultValue: 'No' })}
        </button>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.box, compact && styles.compact)}
      onClick={stop}
    >
      <button
        type="button"
        className={styles.cashout}
        disabled={!offer.available}
        onClick={() => setStep({ kind: 'confirm', amount })}
        title={offer.available ? undefined : t('cash_out_unavailable_label')}
      >
        {t('cash_out_label')}
        {offer.available && <strong>{money(amount)}</strong>}
        {!offer.available && (
          <small className={styles.reason}>
            {t(`cash_out_reason_${offer.reason ?? 'unpriced'}_label`, {
              defaultValue: t('cash_out_unavailable_label'),
            })}
          </small>
        )}
      </button>
    </div>
  );
};
